import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment-timezone'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { MATCH_STYLE, MatchStrategy } from 'apps/booking/types/carrierMatches'
import { sidebarShipmentLaneDateSelector } from 'apps/booking/utils/selectors/sidebar'
import { LaneArrowIcon, ShipmentsIcon, LocationPinIcon } from 'transfix-icons'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui'
import { capitalize } from 'utils/capitalize'
import { formatCurrency } from 'utils/formatCurrency'
import { getTimeAgoShort } from 'utils/formatDatetime'
import DATETIME from 'constants/datetime'
import { IMatchRow } from './MatchRow'

const MAX_STRING_LEN = 18

export const RowMetadata = memo<IMatchRow>(({ match }) => {
  const {
    delivery: deliveryCityStateShipmentFallback,
    pickup: pickupCityStateShipmentFallback,
  } = useSelector(sidebarShipmentLaneDateSelector)
  const {
    carrierId,
    headquarter,
    lastRan,
    lastRate,
    metadata: {
      deliveryCityState,
      destinationLocations,
      externallyCreatedAt,
      originDeadhead,
      pickupCityState,
    },
    strongestMatch,
    totalRan,
  } = match
  const metadata = [
    totalRan ? `Ran ${Number(totalRan)}x` : 'Ran 0x',
    lastRate ? `${formatCurrency(lastRate)}` : undefined,
    lastRan ? `${moment.tz(DATETIME.defaultTimeZone).diff(lastRan, 'days')}d ago` : undefined,
  ]
    .filter(k => k)
    .join(' · ')
  const formatHq = `${capitalize(headquarter.split(',')[0])}, ${headquarter.split(',')[1]}`
  const pickup = pickupCityState || pickupCityStateShipmentFallback
  const delivery = deliveryCityState || deliveryCityStateShipmentFallback
  const pickupLaneElements = [`${pickup} `, <LaneArrowIcon size='large' className='inline' />]

  let matchLabel = String(strongestMatch)
  let laneElement = [...pickupLaneElements, ` ${delivery}`]

  if (strongestMatch === MatchStrategy.postedTrucks) {
    const desiredDeliveryStates = destinationLocations?.split(',').join(', ')
    const hasNoStatesOrCity = !destinationLocations && !deliveryCityState
    const hasStatesOrCity = destinationLocations || deliveryCityState

    const shortenToTooltip = (destinationStr: string) => {
      return destinationStr.length > MAX_STRING_LEN ? (
        <Tooltip
          id={`${carrierId}-destination-location`}
          position='top'
          trigger={<span className='ml1'>{destinationStr.substring(0, MAX_STRING_LEN)}...</span>}>
          {destinationStr}
        </Tooltip>
      ) : (
        <>{destinationStr}</>
      )
    }

    let destination = <></>
    if (deliveryCityState && !destinationLocations) {
      // Displays desired exact delivery City, State
      destination = <>{deliveryCityState}</>
    } else if (!deliveryCityState && destinationLocations) {
      // Displays desired delivery states
      destination = shortenToTooltip(desiredDeliveryStates || '')
    } else if (deliveryCityState && destinationLocations) {
      // Displays both exact delivery City, State and other valid States
      destination = shortenToTooltip(`${deliveryCityState} - ${desiredDeliveryStates}`)
    }

    laneElement = [
      ...pickupLaneElements,
      hasNoStatesOrCity ? <span className='ml1'>Any</span> : <></>,
      hasStatesOrCity ? destination : <></>,
    ]
    matchLabel = `${strongestMatch} · ${getTimeAgoShort(externallyCreatedAt)}`
  } else if (strongestMatch === MatchStrategy.reload) {
    laneElement = [`${delivery}`]
  }

  const shouldDisplayHQ = [MatchStrategy.ranBackhaul, MatchStrategy.ranHeadhaul].includes(
    strongestMatch
  )

  return (
    <>
      <div>
        <Tag
          label={matchLabel}
          size={TAG_SIZE.xsmall}
          tagStyle={MATCH_STYLE[strongestMatch] || TAG_STYLE.accentBlue}
          textStyle='text-inverse'
        />
        <span className='mh2'>{laneElement}</span>
        {shouldDisplayHQ && (
          <Tag
            label={`HQ: ${formatHq}`}
            size={TAG_SIZE.xsmall}
            tagStyle={TAG_STYLE.light}
            textStyle='text-secondary'
          />
        )}
      </div>
      <div className='text-secondary text-small b mt2'>
        {strongestMatch === MatchStrategy.postedTrucks && (
          <span className='mr2'>
            <LocationPinIcon size='medium' className='inline' color='skyBlue6' />{' '}
            {originDeadhead?.miles} mi
          </span>
        )}
        <ShipmentsIcon className='inline' size='medium' /> {metadata}
      </div>
    </>
  )
})
